import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Flex, Layout, Menu, theme } from '@robinpowered/ui-kit';
import { useSessionStorage } from 'react-use';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useNavigation } from '../../hooks/useNavigation';
import {
  INTERCOM_PADDING_COLLAPSED,
  INTERCOM_PADDING_EXPANDED,
  useIntercom,
} from '../../hooks/useIntercom';
import { OrganizationDropdown } from '../OrganizationDropdown/OrganizationDropdown';
import { BottomMenu } from '../BottomMenu/BottomMenu';
import { useFeatureFlags, usePendo } from '@robinpowered/dashboard-apps-common';
import { UserDefaultRoleSlug } from '../../generated/graphql';
import { useAuthContext } from 'contexts/AuthContext';
import { useAmplitude } from 'contexts/AmplitudeContext';
import { AmplitudeEvents } from '../../constants/amplitudeEvents';
import { SkipLink } from './components/SkipLink';
import { MenuSkeleton, BottomMenuSkeleton } from './components/MenuSkeleton';
const { Sider } = Layout;

const SEARCH_MENU_KEY = 'search';
const COLLAPSED_WIDTH = 80;
const TOGGLE_BUTTON_SIZE = 28;

const SidebarWrapper = styled.div`
  position: relative;
  height: 100%;
`;

/** Bottom stack (profile / getting started): holds the divider and collapse toggle on the seam. */
const BottomMenuSection = styled.div`
  position: relative;
  margin-top: auto;
  border-top: 1px solid #424242;
  /* Lower half of the toggle sits inside this section — clear content below it */
  padding-top: ${TOGGLE_BUTTON_SIZE / 2}px;
`;

const MenuWrapper = styled.div`
  && .ant-menu-submenu > .ant-menu-submenu-title:hover {
    color: rgba(255, 255, 255, 0.85);
  }

  && .ant-menu-item.nav-search-item {
    border-bottom: 1px solid #424242;
    border-radius: 0;
    margin-bottom: 12px;
  }

  .ant-menu-item:focus-visible,
  .ant-menu-submenu-title:focus-visible,
  .ant-menu-item:has(:focus-visible),
  .ant-menu-submenu-title:has(:focus-visible) {
    outline: 2px solid rgba(255, 255, 255, 0.9);
    outline-offset: -2px;
  }
  .ant-menu-submenu:has(.ant-menu-item:focus-visible),
  .ant-menu-submenu:has(.ant-menu-submenu-title:focus-visible) {
    outline: 2px solid rgba(255, 255, 255, 0.9);
    outline-offset: -2px;
  }
`;

const CollapseToggleButton = styled.button`
  position: absolute;
  right: -${TOGGLE_BUTTON_SIZE / 2}px;
  top: 0;
  transform: translateY(-50%);
  width: ${TOGGLE_BUTTON_SIZE}px;
  height: ${TOGGLE_BUTTON_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #1f1f1f;
  border: 1px solid #424242;
  border-radius: 50%;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.85);
  opacity: 0;
  transition: opacity 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &[data-show='true'] {
    opacity: 1;
  }

  &:hover {
    background-color: #2a2a2a;
    color: white;
    transform: translateY(-50%) scale(1.1);
  }

  &:focus-visible {
    opacity: 1;
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
    transform: translateY(-50%);
  }
`;

interface NavSidebarProps {
  onOpenSearch: () => void;
}

export const NavSidebar = ({ onOpenSearch }: NavSidebarProps) => {
  const { t } = useTranslation('globalNavigation');
  const { checkFlag } = useFeatureFlags();
  const showPendo = checkFlag('hydra(global-nav-pendo)', false);
  const showIntercom = checkFlag('hydra(global-nav-intercom)', false);
  const {
    currentUser,
    userRoles,
    userPermissions,
    currentOrg,
    belongsToMultipleOrgs,
    loading,
    error: authError,
  } = useAuthContext();

  const { useToken } = theme;
  const { token } = useToken();

  const {
    canApproveSpaceRequests,
    hasPendingApprovalNotifications,
    isLoading: permissionsLoading,
  } = userPermissions || {};
  const showNotifications = canApproveSpaceRequests && currentOrg;
  const showSkeleton = loading || !!authError || permissionsLoading;
  const {
    menuItems,
    activeKeys,
    openKeys,
    gettingStartedLink,
    onboardingProgress,
  } = useNavigation();
  const isSearchEnabled = checkFlag('foundation(search)', false);
  const searchMenuItem = useMemo(
    () =>
      isSearchEnabled
        ? {
            key: SEARCH_MENU_KEY,
            label: t('search'),
            icon: <FontAwesomeIcon icon={faMagnifyingGlass} width="16" />,
            className: 'nav-search-item',
          }
        : null,
    [isSearchEnabled, t]
  );
  const { trackEvent } = useAmplitude();

  // Pendo trial tracking script
  usePendo({
    visitor: currentUser
      ? {
          id: currentUser?.id,
          email: currentUser?.primaryEmail?.email,
          firstName: currentUser?.name?.split(' ')[0],
          lastName: currentUser?.name?.split(' ').slice(1).join(' '),
          roles: userRoles?.map((role: UserDefaultRoleSlug) =>
            role.toLowerCase()
          ),
        }
      : null,
    account: currentOrg
      ? {
          id: currentOrg.id,
          name: currentOrg.name,
        }
      : null,
    isLoading: loading,
    error: !!authError,
  });

  // Use localStorage to persist collapsed state
  const [collapsed, setCollapsed] = useSessionStorage(
    'robin-global-nav-sidebar-collapsed',
    false
  );
  useIntercom();

  const [localOpenKeys, setLocalOpenKeys] = useState<string[]>([]);
  const [isHovered, setIsHovered] = useState(false);

  // Initialize open keys from navigation hook and update when active keys change
  // This sets which menu items are highlighted
  // When sidebar is collapsed, don't auto-open submenus
  useEffect(() => {
    if (collapsed) {
      setLocalOpenKeys([]);
    } else {
      setLocalOpenKeys(openKeys);
    }
  }, [openKeys, collapsed]);

  const handleToggleCollapse = useCallback(() => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);

    if (typeof window.Intercom === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).Intercom('update', {
        horizontal_padding: newCollapsed
          ? INTERCOM_PADDING_COLLAPSED
          : INTERCOM_PADDING_EXPANDED,
      });
    }

    trackEvent(AmplitudeEvents.CLICK_NAV_OPEN_CLOSE_BUTTON, {
      action: newCollapsed ? 'collapse' : 'expand',
    });
  }, [collapsed, setCollapsed, trackEvent]);

  const handleOpenChange = (keys: string[]) => {
    // Track expand/collapse for Workplace nav item
    const workplaceKey = 'workplace.title';
    const wasWorkplaceOpen = localOpenKeys.includes(workplaceKey);
    const isWorkplaceOpen = keys.includes(workplaceKey);
    if (wasWorkplaceOpen !== isWorkplaceOpen) {
      trackEvent(AmplitudeEvents.CLICK_WORKPLACE_NAV_ITEM, {
        action: isWorkplaceOpen ? 'expand' : 'collapse',
      });
    }

    // Track expand/collapse for Space management nav item
    const spaceManagementKey = 'spaceManagement.title';
    const wasSpaceManagementOpen = localOpenKeys.includes(spaceManagementKey);
    const isSpaceManagementOpen = keys.includes(spaceManagementKey);
    if (wasSpaceManagementOpen !== isSpaceManagementOpen) {
      trackEvent(AmplitudeEvents.CLICK_SPACE_MANAGEMENT_NAV_ITEM, {
        action: isSpaceManagementOpen ? 'expand' : 'collapse',
      });
    }

    setLocalOpenKeys(keys);
  };

  const menuItemsWithSearch = searchMenuItem
    ? [searchMenuItem, ...menuItems]
    : menuItems;

  return (
    <>
      <SkipLink focusColor={token.colorPrimary} href="#singleSpaRoot">
        {t('skipToMainContent')}
      </SkipLink>
      <Sider
        collapsed={collapsed}
        collapsedWidth={COLLAPSED_WIDTH}
        trigger={null}
        width={230}
      >
        <SidebarWrapper
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Flex vertical style={{ height: '100%' }}>
            <OrganizationDropdown
              loading={loading}
              error={!!authError}
              belongsToMultipleOrgs={belongsToMultipleOrgs}
              currentOrg={
                currentOrg
                  ? {
                      id: currentOrg.id,
                      name: currentOrg.name ?? '',
                      slug: currentOrg.slug ?? '',
                      monochromeLogo: currentOrg.monochromeLogo ?? '',
                      avatar: currentOrg.avatar ?? '',
                    }
                  : undefined
              }
              collapsed={collapsed}
            />

            <MenuWrapper>
              {showSkeleton ? (
                <MenuSkeleton collapsed={!!collapsed} />
              ) : (
                <Menu
                  style={{ flex: 1, overflowY: 'auto' }}
                  mode="inline"
                  theme="dark"
                  selectedKeys={activeKeys}
                  openKeys={localOpenKeys}
                  onOpenChange={handleOpenChange}
                  items={menuItemsWithSearch}
                  inlineCollapsed={!!collapsed}
                  onClick={({ key }) => {
                    if (key === SEARCH_MENU_KEY) {
                      onOpenSearch();
                    }
                  }}
                />
              )}
            </MenuWrapper>

            <BottomMenuSection>
              <CollapseToggleButton
                data-show={isHovered ? 'true' : 'false'}
                onClick={handleToggleCollapse}
                aria-label={
                  collapsed ? t('expandNavSidebar') : t('collapseNavSidebar')
                }
                title={
                  collapsed ? t('expandNavSidebar') : t('collapseNavSidebar')
                }
              >
                <FontAwesomeIcon
                  icon={collapsed ? faChevronRight : faChevronLeft}
                  size="xs"
                />
              </CollapseToggleButton>
              {showSkeleton ? (
                <BottomMenuSkeleton collapsed={!!collapsed} />
              ) : (
                <BottomMenu
                  name={currentUser?.name ?? ''}
                  avatar={currentUser?.avatar ?? ''}
                  collapsed={!!collapsed}
                  showNotifications={!!showNotifications}
                  hasPendingApprovalNotifications={
                    hasPendingApprovalNotifications
                  }
                  activeOrgSlug={currentOrg?.slug ?? undefined}
                  activeKeys={activeKeys}
                  currentUser={currentUser}
                  showPendo={showPendo}
                  gettingStartedLink={gettingStartedLink}
                  onboardingProgress={onboardingProgress}
                  showIntercom={showIntercom}
                />
              )}
            </BottomMenuSection>
          </Flex>
        </SidebarWrapper>
      </Sider>
    </>
  );
};
